<script setup lang="ts">
import { computed } from 'vue'
import { NPopover } from 'naive-ui'
import type { Tone } from '../tokens'
import type { ScopeNote } from '../types'
import { toneVars } from '../tone-vars'
import NavIcon from './NavIcon.vue'

/**
 * 「口径说明」浮层。
 *
 * 这事关一条容易被忽略的纪律:**原来那些解释性文案一个字都没删**,
 * 只是从正文搬进了浮层。它们交代的是范围(「本期没有视频」「这个数字只算本页」),
 * 演示时被问到就得答得上来 —— 删掉它们等于把一句「我们知道自己没做什么」的自证
 * 换成了「看起来像忘了做」。
 *
 * 触发器是一颗 i 图标 + 一行小字,鼠标不用移出正文就能看出这里有个说明。
 */
const props = withDefaults(
  defineProps<{ notes: readonly ScopeNote[]; tone?: Tone; label?: string }>(),
  { tone: 'light', label: '口径说明' },
)

const vars = computed(() => toneVars(props.tone))
</script>

<template>
  <n-popover trigger="click" placement="bottom-end" :width="460" :style="vars" class="notes-popover">
    <template #trigger>
      <button class="trigger" type="button">
        <NavIcon name="info" :size="13" />
        <span>{{ props.label }}</span>
      </button>
    </template>

    <!-- 变量也挂在内容上:气泡是 teleport 到 body 的,脱离外层根元素的继承链 -->
    <div class="notes" :style="vars">
      <section v-for="(note, index) in props.notes" :key="index" class="note">
        <h4 class="note-title">{{ note.title }}</h4>
        <p class="note-body">{{ note.body }}</p>
        <div v-if="note.refs && note.refs.length > 0" class="refs">
          <span class="refs-label">决策依据</span>
          <span v-for="ref in note.refs" :key="ref" class="ref">{{ ref }}</span>
        </div>
      </section>
    </div>
  </n-popover>
</template>

<style scoped>
.trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  height: 22px;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius-pill);
  background: transparent;
  color: var(--sd-text3);
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  transition: color 160ms ease, border-color 160ms ease;
}

.trigger:hover {
  color: var(--sd-brand);
  border-color: var(--sd-brand-border);
}

.notes {
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: var(--sd-text1);
}

.note {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.note + .note {
  padding-top: 14px;
  border-top: 1px solid var(--sd-border);
}

.note-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--sd-text1);
}

.note-body {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.7;
  color: var(--sd-text2);
  white-space: pre-line;
}

.refs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.refs-label {
  font-size: 11px;
  color: var(--sd-text3);
}

.ref {
  font-family: var(--sd-font-mono);
  font-size: 11px;
  line-height: 18px;
  padding: 0 7px;
  border-radius: var(--sd-radius-pill);
  color: var(--sd-text3);
  border: 1px solid var(--sd-border);
}
</style>
